// src/views/pages/restaurant-reservations/waitlist/components/ReassignModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Alert, Divider, Space, Button, Select, Tag } from "antd";
import { Table as TableIcon, MapPin, Users, AlertTriangle } from "react-feather";

export default function ReassignModal({
  reassignOpen,
  setReassignOpen,
  selected,
  locations = [],
  fetchAvailableTables,
  reassignTable,
  safeNotify,
}) {
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState([]);
  const [chosenId, setChosenId] = useState(null);

  const partySize = Math.max(Number(selected?.partySize || 2), 1);
  const preferredLoc = selected?.location || null;

  // normalize status
  const normalizeStatus = (s) => String(s || "").trim().toLowerCase();

  useEffect(() => {
    if (!reassignOpen) return;

    let alive = true;
    setLoading(true);
    setChosenId(null);

    Promise.resolve(fetchAvailableTables?.())
      .then((rows) => {
        if (!alive) return;
        const list = Array.isArray(rows) ? rows : [];

        // normalize table shape defensively
        const norm = list.map((t) => ({
          id: t.id,
          label: t.label || t.table_no || `Table ${t.id}`,
          capacity: Number(t.capacity || t.seating_capacity || 2),
          location: t.location || t.table_location || "Main Hall",
          status: normalizeStatus(t.status || t.current_status || "available"),
        }));

        // keep only available
        setAvailable(norm.filter((t) => t.status === "available"));
      })
      .catch(() => {
        if (!alive) return;
        safeNotify?.("error", "Failed to load available tables");
        setAvailable([]);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [reassignOpen, fetchAvailableTables]);

  // --- Tiered suggestions
  const tier1 = useMemo(() => {
    // same location + capacity ok
    return available.filter((t) => t.capacity >= partySize && (!preferredLoc || t.location === preferredLoc));
  }, [available, partySize, preferredLoc]);

  const tier2 = useMemo(() => {
    // capacity ok any location
    return available.filter((t) => t.capacity >= partySize);
  }, [available, partySize]);

  const tier3 = useMemo(() => {
    // any available (even if small)
    return available;
  }, [available]);

  const options = useMemo(() => {
    const pick = tier1.length ? tier1 : tier2.length ? tier2 : tier3;
    return pick.slice(0, 12);
  }, [tier1, tier2, tier3]);

  // default choose best
  useEffect(() => {
    if (!reassignOpen) return;
    const best = options[0]?.id;
    setChosenId(best ? String(best) : null);
  }, [reassignOpen, options]);

  const chosen = useMemo(() => options.find((t) => String(t.id) === String(chosenId)) || null, [options, chosenId]);

  const capacityWarning = chosen && chosen.capacity < partySize;

  const assign = async (tableId) => {
    if (!selected?.id) return safeNotify?.("error", "No selected guest");
    if (!tableId) return safeNotify?.("warning", "Please select a table");

    try {
      await reassignTable(selected, tableId);
      setReassignOpen(false);
    } catch {
      // reassignTable already notifies
    }
  };

  const assignBest = async () => {
    const best = (tier1[0] || tier2[0] || tier3[0])?.id;
    if (!best) return safeNotify?.("warning", "No available tables right now.");
    await assign(String(best));
  };

  return (
    <Modal
      open={reassignOpen}
      onCancel={() => setReassignOpen(false)}
      title="Reassign Table"
      okText="Assign"
      okButtonProps={{ disabled: !chosenId, onClick: () => assign(chosenId) }}
      cancelText="Cancel"
      confirmLoading={loading}
      width={620}
    >
      <Alert
        type="info"
        showIcon
        message="We’ll suggest the best available tables by matching party size and preferred location first."
        style={{ borderRadius: 12 }}
      />

      <Divider />

      <Space direction="vertical" style={{ width: "100%" }} size={10}>
        <Space wrap>
          <Tag icon={<Users size={14} />}>{partySize} guests</Tag>
          <Tag icon={<MapPin size={14} />}>{preferredLoc || "Any location"}</Tag>
        </Space>

        {available.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            message="No available tables right now."
            style={{ borderRadius: 12 }}
          />
        ) : (
          <>
            <div style={{ fontWeight: 800 }}>Available Options</div>

            <Space wrap>
              {options.map((t) => {
                const selectedChip = String(chosenId) === String(t.id);
                const tooSmall = t.capacity < partySize;

                return (
                  <Button
                    key={t.id}
                    type={selectedChip ? "primary" : "default"}
                    icon={<TableIcon size={14} />}
                    style={{ borderRadius: 12, height: 38 }}
                    onClick={() => setChosenId(String(t.id))}
                  >
                    {t.label} ({t.capacity}) • {t.location}
                    {tooSmall ? <span style={{ marginLeft: 8, opacity: 0.8 }}>⚠️</span> : null}
                  </Button>
                );
              })}
            </Space>

            <Select
              value={chosenId || undefined}
              onChange={(v) => setChosenId(String(v))}
              placeholder="Or select from list"
              style={{ width: "100%" }}
              options={options.map((t) => ({
                value: String(t.id),
                label: `${t.label} (${t.capacity}) • ${t.location}${t.capacity < partySize ? " — may be small" : ""}`,
              }))}
            />

            {capacityWarning ? (
              <Alert
                type="warning"
                showIcon
                icon={<AlertTriangle size={16} />}
                message="Selected table may be smaller than the party size."
                style={{ borderRadius: 12 }}
              />
            ) : null}

            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={() => setReassignOpen(false)}>Close</Button>
              <Button type="primary" onClick={assignBest} disabled={!available.length}>
                Assign Best Available
              </Button>
            </Space>
          </>
        )}
      </Space>
    </Modal>
  );
}

ReassignModal.propTypes = {
  reassignOpen: PropTypes.bool.isRequired,
  setReassignOpen: PropTypes.func.isRequired,
  selected: PropTypes.object,
  locations: PropTypes.array,
  fetchAvailableTables: PropTypes.func.isRequired,
  reassignTable: PropTypes.func.isRequired,
  safeNotify: PropTypes.func.isRequired,
};
